import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MobileConfig, NodeType, OUNode, Site } from '../types';
import { INITIAL_MOBILE_CONFIGS, INITIAL_NODES, INITIAL_SITES } from '../data/mockData';
import { useOrgNodes } from '../hooks/useOrgNodes';
import { useSites } from '../hooks/useSites';

const SESSION_KEY = 'org-assignment-state';

interface SessionData {
  nodes: OUNode[];
  sites: Site[];
  globalMobileConfigId: string | null;
}

function readSession(): SessionData | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionData) : null;
  } catch {
    return null;
  }
}

interface DraftState {
  sites: Site[];
  nodes: OUNode[];
  globalMobileConfigId: string | null;
}

interface OrgDataContextValue {
  nodes: OUNode[];
  addNode: (name: string, type: NodeType, parentId: string | null) => string;
  renameNode: (id: string, name: string) => void;
  convertType: (id: string, currentNodes: OUNode[]) => boolean;
  convertTypeForce: (id: string) => void;
  getDescendantIds: (id: string, currentNodes: OUNode[]) => string[];
  deleteNode: (id: string) => void;
  deleteNodeCascade: (id: string) => void;
  reparentNode: (id: string, newParentId: string | null) => void;
  reorderNode: (draggedId: string, targetId: string, position: 'before' | 'after', targetParentId: string | null) => void;
  setMobileConfig: (nodeId: string, configId: string | null) => void;
  sites: Site[];
  assignSite: (siteId: string, nodeId: string | null) => void;
  unassignSite: (siteId: string) => void;
  unassignAllFromNode: (nodeId: string) => void;
  createAndAssignSite: (
    siteId: string,
    nodeName: string,
    nodeType: NodeType,
    nodeParentId: string | null,
    addNodeFn: (name: string, type: NodeType, parentId: string | null) => string,
  ) => void;
  bulkAssignSites: (siteIds: string[], nodeId: string) => void;
  mobileConfigs: MobileConfig[];
  globalMobileConfigId: string | null;
  setGlobalMobileConfigId: React.Dispatch<React.SetStateAction<string | null>>;
  isDirty: boolean;
  draft: DraftState | null;
  saveDraft: () => void;
  resetPrototype: () => void;
}

const OrgDataContext = createContext<OrgDataContextValue | null>(null);

export const OrgDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Read once from sessionStorage on mount so state survives page refreshes and HMR reloads
  const [initialData] = useState<SessionData | null>(readSession);

  const {
    nodes, setNodes, addNode, renameNode, convertType, convertTypeForce, getDescendantIds,
    deleteNode, deleteNodeCascade, reparentNode, reorderNode, setMobileConfig,
  } = useOrgNodes(initialData?.nodes ?? INITIAL_NODES);

  const {
    sites, setSites, assignSite, unassignSite, unassignAllFromNode, createAndAssignSite, bulkAssignSites,
  } = useSites(initialData?.sites ?? INITIAL_SITES);

  const [mobileConfigs] = useState<MobileConfig[]>(INITIAL_MOBILE_CONFIGS);
  const [globalMobileConfigId, setGlobalMobileConfigId] = useState<string | null>(
    initialData?.globalMobileConfigId ?? null,
  );
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const isFirstRender = useRef(true);
  const isResetting = useRef(false);

  // Sync to sessionStorage and mark dirty on any change
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ nodes, sites, globalMobileConfigId }));
    if (isResetting.current) {
      isResetting.current = false;
      setIsDirty(false);
      return;
    }
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsDirty(true);
  }, [nodes, sites, globalMobileConfigId]);

  const saveDraft = useCallback(() => {
    setDraft({ sites: [...sites], nodes: [...nodes], globalMobileConfigId });
    setIsDirty(false);
  }, [sites, nodes, globalMobileConfigId]);

  const resetPrototype = useCallback(() => {
    isResetting.current = true;
    setNodes(INITIAL_NODES);
    setSites(INITIAL_SITES);
    setGlobalMobileConfigId(null);
    setDraft(null);
    sessionStorage.removeItem(SESSION_KEY);
  }, [setNodes, setSites]);

  return (
    <OrgDataContext.Provider value={{
      nodes, addNode, renameNode, convertType, convertTypeForce, getDescendantIds,
      deleteNode, deleteNodeCascade, reparentNode, reorderNode, setMobileConfig,
      sites, assignSite, unassignSite, unassignAllFromNode, createAndAssignSite, bulkAssignSites,
      mobileConfigs, globalMobileConfigId, setGlobalMobileConfigId,
      isDirty, draft, saveDraft, resetPrototype,
    }}>
      {children}
    </OrgDataContext.Provider>
  );
};

export const useOrgData = (): OrgDataContextValue => {
  const ctx = useContext(OrgDataContext);
  if (!ctx) throw new Error('useOrgData must be used within OrgDataProvider');
  return ctx;
};
